import { userPool } from "../config/auth";

const API_URL = process.env.REACT_APP_API_URL;

export async function getFreshToken() {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) return reject(new Error("No current user"));
    cognitoUser.getSession((err, session) => {
      if (err || !session.isValid()) return reject(err || new Error("Invalid session"));
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

export async function apiFetch(path, token, options = {}) {
  let freshToken = token;
  try { freshToken = await getFreshToken(); } catch (_) { /* use passed token as fallback */ }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${freshToken}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const text = await res.text();
    const err = new Error(`API error ${res.status}: ${text}`);
    err.status = res.status;
    try { err.data = JSON.parse(text); } catch (_) {}
    throw err;
  }
  return res.json();
}

// ── Products ──────────────────────────────────────────────────────────────────
export async function apiGetProducts(token) {
  return apiFetch("/products", token);
}

export async function apiCreateProduct(token, product) {
  return apiFetch("/products", token, {
    method: "POST",
    body: JSON.stringify(product),
  });
}

export async function apiUpdateProduct(token, productId, updates) {
  return apiFetch(`/products/${encodeURIComponent(productId)}`, token, {
    method: "PUT",
    body: JSON.stringify(updates),
  });
}

// ── Transactions ──────────────────────────────────────────────────────────────
export async function apiPostTransaction(token, tx) {
  return apiFetch("/transactions", token, {
    method: "POST",
    body: JSON.stringify(tx),
  });
}

export async function apiGetTransactions(token, filters = {}) {
  const params = new URLSearchParams();
  if (filters.workOrderId) params.set("workOrderId", filters.workOrderId);
  if (filters.deliveryId) params.set("deliveryId", filters.deliveryId);
  if (filters.type) params.set("type", filters.type);
  if (filters.limit) params.set("limit", filters.limit);
  const qs = params.toString();
  return apiFetch(`/transactions${qs ? `?${qs}` : ""}`, token);
}

// ── Inventory / Scan ──────────────────────────────────────────────────────────
export async function apiScanBarcode(token, barcodeId) {
  return apiFetch(`/inventory/scan/${encodeURIComponent(barcodeId)}`, token);
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
export async function apiGetDashboardUsage(token) {
  return apiFetch("/inventory/dashboard", token);
}

// ── Work Orders ───────────────────────────────────────────────────────────────
export async function apiCreateWorkOrder(token, workOrder) {
  return apiFetch("/workorders", token, {
    method: "POST",
    body: JSON.stringify(workOrder),
  });
}

export async function apiGetWorkOrder(token, id) {
  return apiFetch(`/workorders/${encodeURIComponent(id)}`, token);
}

export async function apiUpdateWorkOrder(token, id, data) {
  return apiFetch(`/workorders/${encodeURIComponent(id)}`, token, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiCompleteWorkOrder(token, id, items) {
  return apiFetch("/workorders", token, {
    method: "POST",
    body: JSON.stringify({ id, items, status: "completed" }),
  });
}

export async function apiGetWorkOrders(token) {
  return apiFetch("/workorders", token);
}

// ── Deliveries ────────────────────────────────────────────────────────────────
export async function apiCreateDelivery(token, delivery) {
  return apiFetch("/deliveries", token, {
    method: "POST",
    body: JSON.stringify(delivery),
  });
}

export async function apiGetDelivery(token, id) {
  return apiFetch(`/deliveries/${encodeURIComponent(id)}`, token);
}

export async function apiUpdateDelivery(token, id, data) {
  return apiFetch(`/deliveries/${encodeURIComponent(id)}`, token, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function apiCompleteDelivery(token, id, items) {
  return apiFetch("/deliveries", token, {
    method: "POST",
    body: JSON.stringify({ id, items, status: "completed" }),
  });
}

export async function apiGetDeliveries(token) {
  return apiFetch("/deliveries", token);
}
